// @flow
import * as React from 'react';
import { createStore, applyMiddleware, type Store } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import thunk from 'redux-thunk';
import { Provider } from 'react-redux';

import reducer from './reducers';
// types
import type { State } from './reducers';
import type { Action } from './actions';

/* eslint-disable no-use-before-define */
export type GenericGetState<S> = () => S;
export type GenericPromiseAction<A> = Promise<A>;
export type GenericDispatch<A, S> = (
  action: A | GenericThunkAction<A, S> | GenericPromiseAction<A>,
) => any;
export type GenericThunkAction<A, S> = (
  dispatch: GenericDispatch<A, S>,
  getState: GenericGetState<S>,
) => any;
export type GetState = GenericGetState<State>;
export type PromiseAction = GenericPromiseAction<Action>;
export type Dispatch = GenericDispatch<Action, State>;
export type ThunkAction = GenericThunkAction<Action, State>;
/* eslint-enable no-use-before-define */
export type { State, Action };

const composeEnhancers = composeWithDevTools({
  trace: process.env.NODE_ENV === 'debug',
});

const initializeStore = (
  preloadedState?: State,
): Store<State, Action, Dispatch> =>
  createStore<State, Action, Dispatch>(
    reducer,
    preloadedState,
    composeEnhancers(applyMiddleware(thunk)),
  );

export const connectComponentToStore =
  (
    store: Store<State, Action, Dispatch>,
  ): (<
    Props: {},
    DefaultProps,
    Config: React.Config<Props, DefaultProps>,
    C: React.AbstractComponent<Config, any>,
  >(
    C,
  ) => React.AbstractComponent<Config, any>) =>
  <
    Props: {},
    DefaultProps,
    Config: React.Config<Props, DefaultProps>,
    C: React.AbstractComponent<Config, any>,
  >(
    Component: C,
  ): React.AbstractComponent<Config, any> =>
    React.forwardRef<Config, C>((props: Config, ref) => (
      <Provider store={store}>
        <Component {...props} ref={ref} />
      </Provider>
    ));

export default initializeStore;

// eslint-disable-next-line fp/no-let
let instance = null;

export const getInstance = (): Store<State, Action, Dispatch> => {
  if (!instance) {
    // eslint-disable-next-line fp/no-mutation
    instance = initializeStore();
  }

  return instance;
};

export const connectToStore: <
  Props: {},
  DefaultProps,
  Config: React.Config<Props, any>,
  C: React.AbstractComponent<Config>,
>(
  C,
) => React.AbstractComponent<Config> = connectComponentToStore(getInstance());
